import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import PaginationMui from "@mui/lab/Pagination";

import sh from "bundles/common/utils/sh";
import trackEvent from "bundles/common/utils/trackEvent";
import BackdropScreen from "bundles/common/components/BackdropScreen";
import ConfirmModal from "bundles/common/components/ConfirmModal";
import CandidateDialog from "bundles/common/components/CandidateDialog";
import DashboardJobOfferDetailPanel from "bundles/Recruiter/components/DashboardV2/JobsContent/DashboardJobOfferDetailPanel";
import {
  formatJobOfferLocations,
  getOfferStatus,
  truncate,
} from "bundles/common/utils/utils";

const FILTERS = [
  { value: "draft", labelId: "recruiter.dashboard.jobs.drafts", countKey: "draftJobCount" },
  { value: "online", labelId: "job_offer.online", countKey: "onlineJobCount" },
  { value: "closed", labelId: "recruiter.dashboard.jobs.closed", countKey: "closedJobCount" },
];

const JobsContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const intl = useIntl();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const [loading, setLoading] = useState(false);
  const [jobOffers, setJobOffers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [selectedTab, setSelectedTab] = useState("online");
  const [selectedJob, setSelectedJob] = useState(null);
  const [onlineJobCount, setOnlineJobCount] = useState(0);
  const [draftJobCount, setDraftJobCount] = useState(0);
  const [closedJobCount, setClosedJobCount] = useState(0);
  const [publishModalOffer, setPublishModalOffer] = useState(null);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [applicantDialogOpen, setApplicantDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [dialogApplications, setDialogApplications] = useState([]);
  const [dialogCandidate, setDialogCandidate] = useState(null);
  const [dialogCandidateLoading, setDialogCandidateLoading] = useState(false);
  const [applicantsListOffer, setApplicantsListOffer] = useState(null);
  const [applicantsListMode, setApplicantsListMode] = useState("all");
  const detailPanelRef = useRef(null);

  const counts = useMemo(
    () => ({ onlineJobCount, draftJobCount, closedJobCount }),
    [onlineJobCount, draftJobCount, closedJobCount]
  );

  const useStyles = makeStyles(() => ({
    filterPill: {
      borderRadius: "999px",
      textTransform: "none",
      fontWeight: 600,
      fontFamily: "Segoe UI Semibold, sans-serif",
      padding: "6px 14px",
      minWidth: 0,
      border: `1px solid ${theme.palette.background.neutral}`,
      color: theme.palette.text.main,
      backgroundColor: theme.palette.common.white,
    },
    filterPillActive: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      borderColor: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
      },
    },
    metricChip: {
      borderRadius: "8px",
      border: `1px solid ${theme.palette.background.neutral}`,
      backgroundColor: "transparent",
      height: "auto",
      "& .MuiChip-label": {
        padding: "4px 10px",
        fontFamily: "Segoe UI Regular, sans-serif",
        fontSize: 12,
      },
    },
    cardShell: {
      borderRadius: "12px",
      border: `1px solid ${theme.palette.background.neutral}`,
      boxShadow: "none",
    },
  }));

  const classes = useStyles();

  const fetchJobOffers = useCallback(
    (offerStatus, nextPage = 1) => {
      setLoading(true);
      sh
        .get("job_offer", {
          authenticity_token: authenticityToken,
          params: {
            offer_status: offerStatus,
            page: nextPage,
          },
        })
        .then((res) => {
          const offers = res.data?.data?.job_offers || [];
          setJobOffers(offers);
          setOnlineJobCount(res.data?.data?.online_job_count ?? 0);
          setDraftJobCount(res.data?.data?.draft_job_count ?? 0);
          setClosedJobCount(res.data?.data?.closed_job_count ?? 0);
          setPageCount(res.data?.pagy?.pages || 1);
          setSelectedJob((prev) => {
            if (!offers.length) return null;
            const params = new URLSearchParams(window.location.search || "");
            const jid = params.get("jid");
            if (jid) {
              const byJid = offers.find((o) => String(o.id) === String(jid));
              if (byJid) return byJid;
            }
            if (prev && offers.some((o) => o.id === prev.id)) return prev;
            return offers[0];
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => setLoading(false));
    },
    [authenticityToken]
  );

  useEffect(() => {
    fetchJobOffers(selectedTab, page);
  }, [selectedTab, page, fetchJobOffers]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const jobsFilter = params.get("jobs_filter");
    if (jobsFilter === "draft" || jobsFilter === "online" || jobsFilter === "closed") {
      setSelectedTab((prev) => (prev !== jobsFilter ? jobsFilter : prev));
      setPage(1);
    }
  }, [location.search]);

  const selectJobsFilter = useCallback(
    (value) => {
      setPage(1);
      setSelectedTab(value);
      const params = new URLSearchParams(location.search || "");
      params.set("tab", "jobs");
      params.set("jobs_filter", value);
      params.delete("jid");
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      trackEvent("jobs", "filter_selected", { filter: value });
    },
    [location.pathname, location.search, navigate]
  );

  const updateCurrentRecruiter = useCallback(
    (recruiter) => {
      dispatch({ type: "UPDATE_CURRENT_RECRUITER", payload: recruiter });
    },
    [dispatch]
  );

  const showSnackbar = (message, variant, autoHideDuration) => {
    enqueueSnackbar(message, { variant, autoHideDuration });
  };

  const handlePublishConfirm = (e) => {
    e?.stopPropagation?.();
    if (!publishModalOffer) return;
    const offer = publishModalOffer;
    sh
      .put(`job_offer/${offer.id}`, {
        authenticity_token: authenticityToken,
        job_offer: offer,
        skills: offer.skills,
        with_publish: true,
      })
      .then((res) => {
        if (res.data.result?.publish_error) {
          showSnackbar(
            intl.formatMessage({ id: "flash.success.job_offer_edition_without_publish" }),
            "warning",
            15000
          );
        } else {
          showSnackbar(
            intl.formatMessage({ id: "flash.success.job_offer_edition_with_publish" }),
            "success",
            2000
          );
          updateCurrentRecruiter(res.data.recruiter);
          setSelectedTab("online");
          setPage(1);
        }
      })
      .catch(() => {
        showSnackbar(intl.formatMessage({ id: "flash.unknown" }), "error");
      })
      .finally(() => setPublishModalOffer(null));
  };

  const handleCloseOfferConfirmed = () => {
    if (!selectedJob) return;
    sh
      .delete(`job_offer/${selectedJob.id}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        data: { authenticity_token: authenticityToken },
      })
      .then((response) => {
        const days_left = response.data?.days_left;
        const message =
          typeof days_left === "number"
            ? intl.formatMessage(
                { id: "recruiter.dashboard.jobs.offer_closed_credit" },
                { days: days_left }
              )
            : intl.formatMessage({ id: "recruiter.dashboard.jobs.offer_closed" });
        enqueueSnackbar(message, { variant: "success" });
        setCloseModalOpen(false);
        setSelectedTab("closed");
        setPage(1);
      })
      .catch(() => {
        showSnackbar(intl.formatMessage({ id: "flash.unknown" }), "error");
      });
  };

  // Repost a closed offer "in place": re-publishes the same record so its stats keep
  // accumulating across cycles (consumes another credit). See JobOffers::RenewService.
  const handleRepost = (offer, e) => {
    e?.stopPropagation?.();
    sh
      .post(`recruiter/renew_job_offers/${offer.id}`, {
        authenticity_token: authenticityToken,
      })
      .then(() => {
        showSnackbar(
          intl.formatMessage({ id: "flash.success.job_offer_edition_with_publish" }),
          "success",
          2000
        );
        setSelectedTab("online");
        setPage(1);
      })
      .catch((error) => {
        if (error?.response?.data?.error === "need_credit") {
          showSnackbar(intl.formatMessage({ id: "credits_box.no_job_offer_credits" }), "warning", 6000);
        } else {
          showSnackbar(intl.formatMessage({ id: "flash.unknown" }), "error");
        }
      });
  };

  const fetchCandidateApplications = useCallback(
    (candidateId) => {
      if (!candidateId) {
        setDialogApplications([]);
        return;
      }
      sh
        .get("my_candidates", {
          authenticity_token: authenticityToken,
          params: { candidate_id: candidateId },
        })
        .then((res) => {
          const apps = res.data?.data || [];
          setDialogApplications(apps);
          const fullCandidate = apps.find((a) => a.candidate?.id === candidateId)?.candidate;
          if (fullCandidate) setDialogCandidate(fullCandidate);
        })
        .catch(() => setDialogApplications([]))
        .finally(() => setDialogCandidateLoading(false));
    },
    [authenticityToken]
  );

  const openApplicantDialog = (application) => {
    setSelectedApplication(application);
    setDialogCandidate(application?.candidate || null);
    if (application?.candidate?.id) {
      setDialogCandidateLoading(true);
      fetchCandidateApplications(application.candidate.id);
    } else {
      setDialogApplications([]);
      setDialogCandidateLoading(false);
    }
    setApplicantDialogOpen(true);
  };

  const closeApplicantDialog = () => {
    setApplicantDialogOpen(false);
    setSelectedApplication(null);
    setDialogApplications([]);
    setDialogCandidate(null);
    setDialogCandidateLoading(false);
  };

  const openApplicantsList = (offer, mode, e) => {
    e?.stopPropagation?.();
    setApplicantsListMode(mode);
    setApplicantsListOffer(offer);
  };

  const closeApplicantsList = () => setApplicantsListOffer(null);

  const renderApplicantRow = (app, onView) => {
    const c = app.candidate || {};
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "—";
    const title = c.job_title || c.desired_job_title || "";
    const statusLabel = intl.formatMessage({
      id: `application.${app.status}`,
      defaultMessage: app.status,
    });
    return (
      <Card
        key={app.id}
        elevation={0}
        sx={{
          borderRadius: "10px",
          border: `1px solid ${theme.palette.background.neutral}`,
          bgcolor: "#fff",
        }}
      >
        <CardContent
          sx={{
            py: 1.5,
            px: 2,
            "&:last-child": { pb: 1.5 },
            display: "flex",
            flexDirection: isDesktop ? "row" : "column",
            alignItems: isDesktop ? "center" : "stretch",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
              {name}
            </Typography>
            {title ? (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ fontFamily: "Segoe UI Regular, sans-serif" }}
              >
                {title}
              </Typography>
            ) : null}
            <Chip
              size="small"
              label={statusLabel}
              variant="outlined"
              sx={{ mt: 1, borderRadius: "8px" }}
            />
          </Box>
          <Button
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: "999px",
              textTransform: "none",
              flexShrink: 0,
              fontWeight: 600,
            }}
            onClick={() => onView(app)}
          >
            <FormattedMessage id="recruiter.dashboard.jobs.view_profile" />
          </Button>
        </CardContent>
      </Card>
    );
  };

  const renderApplicantsListDialog = () => {
    if (!applicantsListOffer) return null;
    const apps = (applicantsListOffer.job_applications || []).filter(
      (a) => !a.added_from_cvtheque
    );
    const list =
      applicantsListMode === "new"
        ? apps.filter((a) => a.status === "pending")
        : apps;
    const titleId =
      applicantsListMode === "new"
        ? "recruiter.dashboard.jobs.applicants_new"
        : "recruiter.dashboard.jobs.applicants_all";
    return (
      <Dialog open onClose={closeApplicantsList} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontFamily: "Segoe UI Semibold, sans-serif" }}>
              <FormattedMessage id={titleId} />
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {truncate(applicantsListOffer.job_title, 60)}
            </Typography>
          </Box>
          <IconButton onClick={closeApplicantsList} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {list.map((app) =>
              renderApplicantRow(app, (a) => {
                closeApplicantsList();
                openApplicantDialog(a);
              })
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    );
  };

  const renderStatusChip = (offer) => {
    if (offer.offer_status === "draft") {
      return (
        <Chip
          size="small"
          label={<FormattedMessage id="recruiter.dashboard.jobs.drafts" />}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "#fff",
            fontWeight: 600,
            fontFamily: "Segoe UI Semibold, sans-serif",
            "& .MuiChip-label": { px: 1.25 },
          }}
        />
      );
    }
    if (offer.offer_status === "closed") {
      return (
        <Chip
          size="small"
          label={<FormattedMessage id="recruiter.dashboard.jobs.closed" />}
          variant="outlined"
          sx={{
            borderColor: theme.palette.background.neutral,
            fontWeight: 600,
            fontFamily: "Segoe UI Semibold, sans-serif",
          }}
        />
      );
    }
    const st = getOfferStatus(offer, 5);
    const urgent = st.id === "job_offer_summary.expire_soon";
    return (
      <Chip
        size="small"
        label={<FormattedMessage id={st.id} values={st.values} />}
        sx={{
          backgroundColor: urgent ? theme.palette.primary.main : "#CEE1DA",
          color: urgent ? "#fff" : theme.palette.primary.main,
          fontWeight: 600,
          fontFamily: "Segoe UI Semibold, sans-serif",
          "& .MuiChip-label": { px: 1.25 },
        }}
      />
    );
  };

  useEffect(() => {
    if (!isDesktop) return;
    const el = detailPanelRef.current;
    if (!el) return;

    const TOP_OFFSET = 60;
    const BOTTOM_PADDING = 16;
    let currentTop = TOP_OFFSET;
    let lastScrollY = window.scrollY;

    el.style.position = "sticky";
    el.style.alignSelf = "flex-start";
    el.style.top = `${currentTop}px`;

    const onScroll = () => {
      const scrollY = window.scrollY;
      const delta = scrollY - lastScrollY;
      lastScrollY = scrollY;

      const panelHeight = el.offsetHeight;
      const vh = window.innerHeight;

      if (panelHeight <= vh - TOP_OFFSET - BOTTOM_PADDING) {
        currentTop = TOP_OFFSET;
      } else {
        const topMin = vh - panelHeight - BOTTOM_PADDING;
        currentTop = Math.max(topMin, Math.min(TOP_OFFSET, currentTop - delta));
      }

      el.style.top = `${currentTop}px`;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      el.style.position = "";
      el.style.top = "";
      el.style.alignSelf = "";
    };
  }, [isDesktop, selectedJob?.id]);

  const applications = (selectedJob?.job_applications || []).filter(
    (a) => !a.added_from_cvtheque
  );

  const accentLink = theme.palette.primary.main;

  return (
    <Box
      sx={{
        bgcolor: "#F8F8F8",
        borderRadius: "12px",
        p: { xs: 1.5, md: 2 },
        mx: { xs: -0.5, md: 0 },
      }}
    >
      <BackdropScreen open={loading} />
      <Grid container spacing={{ xs: 2, md: 3 }} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <Card className={classes.cardShell} elevation={0} sx={{ bgcolor: "background.paper" }}>
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isDesktop ? "row" : "column",
                    alignItems: isDesktop ? "center" : "stretch",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant={isDesktop ? "h5" : "h3"}
                    sx={{ fontWeight: 700, fontFamily: "Segoe UI Semibold, sans-serif" }}
                  >
                    <FormattedMessage id="job_offer.my_job_offers" />
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    href={`/${i18nLocale}/recruiter/job_offer_creation`}
                    sx={{
                      borderRadius: "999px",
                      textTransform: "none",
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      height: 40,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormattedMessage id="job_offer.create_new_job_offer" />
                  </Button>
                </Box>
                <Stack direction="row" spacing={1}>
                  {FILTERS.map((f) => (
                    <Button
                      key={f.value}
                      className={`${classes.filterPill} ${
                        selectedTab === f.value ? classes.filterPillActive : ""
                      }`}
                      sx={{ whiteSpace: "nowrap", flex: 1, minWidth: 0, px: { xs: 1, md: "14px" }, fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                      onClick={() => selectJobsFilter(f.value)}
                    >
                      <FormattedMessage id={f.labelId} /> ({counts[f.countKey] ?? 0})
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Box
              sx={
                isDesktop
                  ? {
                    minHeight: 280,
                    overflowY: "auto",
                    pr: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                    "& > *": { flexShrink: 0 },
                  }
                  : { display: "flex", flexDirection: "column", gap: 2.5 }
              }
            >
              {jobOffers.length === 0 && !loading ? (
                <Card className={classes.cardShell} elevation={0} sx={{ bgcolor: "background.paper" }}>
                  <CardContent sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      <FormattedMessage id={`recruiter.no_${selectedTab}_offers`} />
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ borderRadius: "999px", textTransform: "none", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}
                      href={`/${i18nLocale}/recruiter/job_offer_creation`}
                    >
                      <FormattedMessage id={`recruiter.no_${selectedTab}_offers_cta`} />
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                jobOffers.map((offer) => {
                  const selected = selectedJob?.id === offer.id;
                  const apps = (offer.job_applications || []).filter(
                    (a) => !a.added_from_cvtheque
                  );
                  const totalApps = apps.length;
                  const pending = apps.filter((a) => a.status === "pending").length;
                  const locs = formatJobOfferLocations(offer?.locations || []).join(", ");

                  return (
                    <Card
                      key={offer.id}
                      className={classes.cardShell}
                      elevation={0}
                      onClick={() => {
                        setSelectedJob(offer);
                        trackEvent("jobs", "offer_detail_viewed");
                      }}
                      sx={{
                        cursor: "pointer",
                        transition:
                          "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
                        border: selected
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${theme.palette.background.neutral}`,
                        bgcolor: selected
                          ? theme.palette.secondary.light
                          : theme.palette.common.white,
                        boxShadow: selected
                          ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.22)}, 0 6px 18px ${alpha(theme.palette.common.black, 0.07)}`
                          : "none",
                        ...(!selected && {
                          "&:hover": {
                            bgcolor: alpha(theme.palette.common.black, 0.02),
                            boxShadow: "0 2px 8px rgba(2, 51, 49, 0.06)",
                          },
                        }),
                      }}
                    >
                      <CardContent sx={{ py: 2, px: 2, "&:last-child": { pb: 2 } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#8E8E8E",
                            fontSize: 12,
                            mb: 0.5,
                            fontFamily: "Segoe UI Regular, sans-serif",
                          }}
                        >
                          {locs || "—"}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: theme.palette.text.blue,
                            mb: 1.25,
                            fontFamily: "Segoe UI Semibold, sans-serif",
                            lineHeight: 1.35,
                          }}
                        >
                          {truncate(offer.job_title, isDesktop ? 90 : 52)}
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 1,
                            rowGap: 1,
                          }}
                        >
                          {renderStatusChip(offer)}
                          {offer.offer_status === "online" && (
                            <Chip
                              className={classes.metricChip}
                              label={intl.formatMessage(
                                { id: "recruiter.dashboard.jobs.views_short" },
                                { count: offer.view_count ?? 0 }
                              )}
                            />
                          )}
                          <Chip
                            className={classes.metricChip}
                            label={intl.formatMessage(
                              { id: "recruiter.dashboard.jobs.candidates_short" },
                              { count: totalApps }
                            )}
                            onClick={
                              totalApps > 0
                                ? (e) => openApplicantsList(offer, "all", e)
                                : undefined
                            }
                            sx={
                              totalApps > 0
                                ? {
                                  cursor: "pointer",
                                  borderColor: accentLink,
                                  "& .MuiChip-label": {
                                    color: accentLink,
                                    textDecoration: "underline",
                                    fontWeight: 500,
                                  },
                                }
                                : undefined
                            }
                          />
                          {offer.offer_status === "online" && pending > 0 && (
                            <Chip
                              className={classes.metricChip}
                              label={intl.formatMessage(
                                { id: "recruiter.dashboard.jobs.new_applications_short" },
                                { count: pending }
                              )}
                              onClick={(e) => openApplicantsList(offer, "new", e)}
                              sx={{
                                cursor: "pointer",
                                borderColor: accentLink,
                                "& .MuiChip-label": {
                                  color: accentLink,
                                  textDecoration: "underline",
                                  fontWeight: 500,
                                },
                              }}
                            />
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1.5,
                            alignItems: "center",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {offer.offer_status === "draft" && (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                href={`/${i18nLocale}/recruiter/job_offer_creation?jobOffer=${offer.id}`}
                                sx={{ borderRadius: "999px", textTransform: "none", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontWeight: 600 }}
                              >
                                <FormattedMessage id="job_offer.edit" />
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 600 }}
                                onClick={() => {
                                  setPublishModalOffer(offer);
                                  trackEvent("jobs", "publish_modal_opened");
                                }}
                              >
                                <FormattedMessage id="job_offer.publish" />
                              </Button>
                            </>
                          )}
                          {offer.offer_status === "online" && (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                sx={{
                                  borderRadius: "999px",
                                  textTransform: "none",
                                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                                }}
                                href={`/${i18nLocale}/recruiter/job_offer_creation?jobOffer=${offer.id}`}
                              >
                                <FormattedMessage id="job_offer.edit" />
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 600 }}
                                onClick={() => {
                                  setSelectedJob(offer);
                                  setCloseModalOpen(true);
                                }}
                              >
                                <FormattedMessage id="job_offer.soft_delete" />
                              </Button>
                            </>
                          )}
                          {offer.offer_status === "closed" && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              sx={{ borderRadius: "999px", textTransform: "none", fontWeight: 600 }}
                              onClick={(e) => handleRepost(offer, e)}
                            >
                              <FormattedMessage id="job_offer.repost" />
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })
              )}

              {pageCount > 1 && (
                <Card className={classes.cardShell} elevation={0} sx={{ bgcolor: "background.paper" }}>
                  <CardContent sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <PaginationMui
                      size="large"
                      onChange={(e, p) => setPage(p)}
                      count={pageCount}
                      page={page}
                      variant="outlined"
                      color="primary"
                    />
                  </CardContent>
                </Card>
              )}
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} md={7} ref={detailPanelRef}>
          <Stack spacing={2}>
            {selectedJob ? (
              <>
                <DashboardJobOfferDetailPanel
                  jobOffer={selectedJob}
                  skills={selectedJob.skills}
                  onPublish={(j) => setPublishModalOffer(j)}
                  onCloseJob={() => setCloseModalOpen(true)}
                  onDuplicate={() => handleRepost(selectedJob, { stopPropagation() {} })}
                />

                {applications.length > 0 && (
                  <Card className={classes.cardShell} elevation={0} sx={{ bgcolor: "background.paper" }}>
                    <CardContent sx={{ py: 2.5, px: 2.5 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 18,
                          mb: 2,
                          fontFamily: "Segoe UI Semibold, sans-serif",
                          color: "#222",
                        }}
                      >
                        <FormattedMessage id="recruiter.dashboard.jobs.applicants_section" />
                      </Typography>
                      <Stack spacing={1.5}>
                        {applications.map((app) => renderApplicantRow(app, openApplicantDialog))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              !loading && (
                <Card className={classes.cardShell} elevation={0} sx={{ bgcolor: "background.paper" }}>
                  <CardContent>
                    <Typography color="text.secondary" sx={{ fontFamily: "Segoe UI Regular, sans-serif" }}>
                      <FormattedMessage id="recruiter.no_online_offers" />
                    </Typography>
                  </CardContent>
                </Card>
              )
            )}
          </Stack>
        </Grid>
      </Grid>

      <ConfirmModal
        isOpen={Boolean(publishModalOffer)}
        setIsOpen={(open) => !open && setPublishModalOffer(null)}
        handleConfirm={handlePublishConfirm}
        labelId="publish_offer_confirmation"
      />

      <ConfirmModal
        isOpen={closeModalOpen}
        setIsOpen={setCloseModalOpen}
        handleConfirm={handleCloseOfferConfirmed}
        labelId="close_offer_confirmation"
      />

      {renderApplicantsListDialog()}

      {selectedApplication?.candidate && (
        <CandidateDialog
          candidate={dialogCandidate || selectedApplication.candidate}
          candidateLoading={dialogCandidateLoading}
          jobApplication={selectedApplication}
          jobApplications={dialogApplications}
          onClose={closeApplicantDialog}
          isDialogOpen={applicantDialogOpen}
          setIsDialogOpen={setApplicantDialogOpen}
          columns={[]}
          currentColumnId={null}
          onMoveToColumn={null}
          isModal
        />
      )}
    </Box>
  );
};

export default JobsContent;
